/**
 * The group copy's two calls (19/10/2026): the review (no writes) is asked
 * again on every choice — the request is the key, and the last answer stays
 * on screen while the next one loads — and the paste posts the very request
 * the review on screen answered. A paste refreshes every Automation read.
 * For Media Devoted | Created By Ciro Autiero
 */
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GroupCopyRequest, GroupCopyResult, GroupCopyReview } from '@trafficpulse/shared';

import { dropGeneratorKeys } from '@/features/drop-generator/useDropGenerator';
import { api } from '@/services/api';

interface Envelope<T> {
  data: T;
}

const BASE = '/drop-generator/group-copy';

/** Its own root: the paste's refresh of the Automation reads must not re-ask a review. */
export const groupCopyKeys = {
  review: (request: GroupCopyRequest | null) => ['drop-generator-group-copy', 'review', request] as const,
};

/** One review's query: the request is the key; the last answer stays on screen while the next loads. */
export const reviewQuery = (request: GroupCopyRequest | null) => ({
  queryKey: groupCopyKeys.review(request),
  queryFn: async () => (await api.post<Envelope<GroupCopyReview>>(`${BASE}/review`, request)).data.data,
  enabled: request !== null,
  placeholderData: keepPreviousData,
  staleTime: 0,
  retry: false,
});

export function useGroupCopyReview(request: GroupCopyRequest | null) {
  return useQuery(reviewQuery(request));
}

/** v2 (26/10/2026) — one review per target country, each asked again on its own choices. */
export function useGroupCopyReviews(requests: Array<GroupCopyRequest | null>) {
  return useQueries({ queries: requests.map(reviewQuery) });
}

export function useGroupCopyPaste() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: GroupCopyRequest) =>
      (await api.post<Envelope<GroupCopyResult>>(`${BASE}/paste`, request)).data.data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dropGeneratorKeys.all });
    },
  });
}
